import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, MessageCircleHeart, FileSignature, ClipboardList, Play, X, ChevronRight, RotateCcw, LogOut, BarChart3 } from "lucide-react";
import { Logo, Loading } from "./Bits";
import { useFamily } from "../context/FamilyContext";
import { login, logout } from "../context/auth";

const TOUR = [
  { path: "/demo", title: "Ana's home", text: "Ana cares for her dad, Lolo Ben. Here is her Break Meter and Respite Balance." },
  { path: "/demo/ask", title: "The Ask", text: "Asking for help is hard. Watch Handoff write a Taglish text to a neighbor." },
  { path: "/demo/agreement", title: "Expectations Agreement", text: "Ana and Marisol wrote down what helps, and what doesn't." },
  { path: "/demo/brief", title: "Handoff Brief", text: "Before a visit, Ana talks or types. Handoff makes a one-page brief." },
  { path: "/demo/helper", title: "Marisol's view", text: "No app, no password. Just a text link." },
  { path: "/demo/visit", title: "Visit mode", text: "Start, notes, and help if something feels off. Then a shift report." },
  { path: "/demo/partner", title: "Program partner", text: "Verified visit hours and rest scores, for the program that pays." },
];

const viewOf = (path) => {
  if (path.startsWith("/demo/partner")) return "partner";
  if (/^\/demo\/(helper|visit|report\/|training|certificates)/.test(path)) return "helper";
  return "caregiver";
};

function ViewToggle() {
  const loc = useLocation();
  const nav = useNavigate();
  const v = viewOf(loc.pathname);
  const opts = [
    ["caregiver", "Caregiver", "/demo"],
    ["helper", "Helper", "/demo/helper"],
    ["partner", "Partner", "/demo/partner"],
  ];
  return (
    <div data-testid="demo-view-toggle" className="flex rounded-full bg-hf-creamdeep p-1">
      {opts.map(([k, label, to]) => (
        <button
          key={k}
          data-testid={`demo-view-${k}`}
          onClick={() => nav(to)}
          className={`min-h-[40px] rounded-full px-3 text-sm font-semibold transition-colors sm:px-4 ${
            v === k ? "bg-hf-green text-hf-cream" : "text-hf-green"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function TourBar() {
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(() => sessionStorage.getItem("handoff_tour") !== "off");
  const idx = TOUR.findIndex((s) => s.path === loc.pathname);
  if (!open || idx < 0) return null;
  const step = TOUR[idx];
  const next = TOUR[idx + 1];
  return (
    <div data-testid="demo-tour-bar" className="mb-6 flex items-start gap-3 rounded-2xl bg-hf-green p-4 text-hf-cream hf-rise">
      <div className="flex-1">
        <p className="text-xs font-semibold uppercase tracking-widest text-hf-sun">
          Guided tour {idx + 1} of {TOUR.length}
        </p>
        <p className="mt-1 font-heading text-lg font-semibold">{step.title}</p>
        <p className="text-base text-hf-cream/85">{step.text}</p>
        {next && (
          <button data-testid="demo-tour-next" onClick={() => nav(next.path)} className="mt-3 inline-flex min-h-[44px] items-center gap-1 rounded-full bg-hf-cream px-4 text-sm font-semibold text-hf-green">
            Next: {next.title} <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
      <button
        aria-label="Hide tour"
        data-testid="demo-tour-close"
        onClick={() => {
          sessionStorage.setItem("handoff_tour", "off");
          setOpen(false);
        }}
        className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-hf-greendark"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

function BottomNav() {
  const { base, mode, paths, data } = useFamily();
  const loc = useLocation();
  const view = mode === "demo" ? viewOf(loc.pathname) : mode === "helper" ? "helper" : "caregiver";
  if (view === "partner") return null;
  const lastBrief = (data?.briefs || []).find((b) => b.status === "approved");
  const items =
    view === "helper"
      ? [
          [paths.helperHome, "Home", Home, "nav-helper-home"],
          [`${base}/visit`, "Visit", Play, "nav-visit"],
          [`${base}/agreement`, "Agreement", FileSignature, "nav-agreement"],
          [lastBrief ? `${base}/briefs/${lastBrief.id}` : `${base}/brief`, "Brief", ClipboardList, "nav-brief"],
        ]
      : [
          [base, "Home", Home, "nav-home"],
          [`${base}/ask`, "The Ask", MessageCircleHeart, "nav-ask"],
          [`${base}/agreement`, "Agreement", FileSignature, "nav-agreement"],
          [`${base}/brief`, "Brief", ClipboardList, "nav-brief"],
        ];
  return (
    <nav className="fixed inset-x-0 bottom-0 z-30 border-t-[1.5px] border-hf-line bg-white pb-[env(safe-area-inset-bottom)]">
      <div className="mx-auto grid max-w-2xl grid-cols-4">
        {items.map(([to, label, Icon, tid]) => (
          <NavLink
            key={tid}
            to={to}
            end
            data-testid={tid}
            className={({ isActive }) =>
              `flex min-h-[64px] flex-col items-center justify-center gap-1 text-[13.5px] font-medium transition-colors ${
                isActive ? "text-hf-green" : "text-hf-muted"
              }`
            }
          >
            <Icon className="h-6 w-6" />
            {label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function Shell() {
  const { mode, data, error } = useFamily();
  if (error === 401 && mode === "app") return <SignInGate />;
  if (error === 401 && mode === "helper") return <HelperGate />;
  if (error === 429) return (
    <div data-testid="demo-busy-state" className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6">
      <Logo />
      <h1 className="mt-8 font-heading text-3xl">The demo is very busy right now.</h1>
      <p className="mt-3 text-lg text-hf-muted">Lots of people are trying it at once. Please come back in a little while.</p>
      <Link to="/" className="hf-btn hf-btn-primary mt-8 w-full">Back to Handoff</Link>
    </div>
  );
  if (error) return <div className="p-8 text-lg">We couldn't open this right now. Please refresh and try again.</div>;
  if (!data) return <Loading label="Opening Handoff..." />;
  const framed = mode === "demo";
  return (
    <div className={framed ? "min-h-screen bg-hf-cream lg:flex lg:items-center lg:justify-center lg:gap-16 lg:px-8 lg:py-10" : ""}>
      {framed && <DemoAside />}
      <div data-testid={framed ? "demo-phone-frame" : undefined} className={framed ? "lg:relative lg:h-[844px] lg:w-[390px] lg:shrink-0 lg:overflow-hidden lg:rounded-[48px] lg:border-[10px] lg:border-hf-ink lg:bg-hf-cream lg:[transform:translateZ(0)] hf-phone-in" : ""}>
        <div className={framed ? "lg:h-full lg:overflow-y-auto" : ""}>
          <ShellBody />
        </div>
      </div>
    </div>
  );
}

function DemoAside() {
  const loc = useLocation();
  return (
    <aside className="hidden max-w-sm lg:block">
      <Logo />
      <p className="hf-eyebrow mt-10">A guided demo</p>
      <h1 className="mt-2 font-heading text-4xl leading-tight">The Santos family</h1>
      <p className="mt-4 text-base leading-relaxed text-hf-muted">Ana cares for her dad, Lolo Ben. Their neighbor Marisol steps in. Everyone here is fictional.</p>
      <ol className="mt-8 space-y-1">
        {TOUR.map((s, i) => (
          <li key={s.path}>
            <Link to={s.path} data-testid={`demo-aside-step-${i + 1}`} className={`flex items-baseline gap-3 rounded-md px-2 py-2 text-[15px] transition-colors ${loc.pathname === s.path ? "bg-white text-hf-green ring-[1.5px] ring-hf-line" : "text-hf-muted hover:text-hf-green"}`}>
              <span className="font-heading text-hf-coraldark">{i + 1}</span> {s.title}
            </Link>
          </li>
        ))}
      </ol>
    </aside>
  );
}

function ShellBody() {
  const { mode, data, resetDemo } = useFamily();
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector("[data-testid=demo-phone-frame] > div")?.scrollTo(0, 0);
  }, [pathname]);
  return (
    <div className="min-h-screen bg-hf-cream pb-36 lg:min-h-full">
      <header className="sticky top-0 z-30 border-b-[1.5px] border-hf-line bg-hf-cream">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-2 px-4 py-3">
          <Logo to={mode === "demo" ? "/" : undefined} compact={mode === "demo"} />
          {mode === "demo" && <ViewToggle />}
          {mode === "app" && (
            <div className="flex items-center gap-1">
              {data.is_partner && <AdminPartnerLink />}
              <button data-testid="sign-out-button" onClick={logout} aria-label="Sign out" className="flex h-11 w-11 items-center justify-center rounded-full text-hf-green hover:bg-hf-sage">
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
        {mode === "demo" ? (
          <div className="bg-hf-sun/30 px-4 py-2 text-center text-sm text-hf-ink" data-testid="demo-banner">
            Demo with a fictional family. Nothing here is real.{" "}
            <button data-testid="demo-reset-button" onClick={resetDemo} className="inline-flex items-center gap-1 font-semibold text-hf-green underline">
              <RotateCcw className="h-3.5 w-3.5" /> Start over
            </button>
          </div>
        ) : (
          <div className="bg-hf-coralsoft px-4 py-2 text-center text-sm text-hf-ink" data-testid="pilot-privacy-banner">
            Pilot version. Don't enter medical record numbers or insurance IDs.
          </div>
        )}
      </header>
      <main className="mx-auto max-w-2xl px-5 pt-6">
        {mode === "demo" && <TourBar />}
        <Outlet />
      </main>
      <BottomNav />
    </div>
  );
}

function AdminPartnerLink() {
  return (
    <Link to="/app/partner" data-testid="partner-dashboard-link" aria-label="Program dashboard" className="flex h-11 w-11 items-center justify-center rounded-full text-hf-green hover:bg-hf-sage">
      <BarChart3 className="h-5 w-5" />
    </Link>
  );
}

function SignInGate() {
  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6">
      <Logo />
      <h1 className="mt-8 font-heading text-3xl font-semibold">Welcome back.</h1>
      <p className="mt-3 text-lg text-hf-muted">Sign in with Google to see your breaks, briefs and reports.</p>
      <button data-testid="signin-google-button" onClick={() => login("/app")} className="hf-btn hf-btn-primary mt-8 w-full">
        Continue with Google
      </button>
      <Link to="/demo" data-testid="signin-see-demo" className="hf-btn hf-btn-ghost mt-3 w-full">
        See the demo instead
      </Link>
    </div>
  );
}

function HelperGate() {
  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6">
      <Logo />
      <h1 className="mt-8 font-heading text-3xl font-semibold">Hi there.</h1>
      <p className="mt-3 text-lg text-hf-muted">Open the link the family texted you, or get a sign-in link by email.</p>
      <Link to="/helper/login" data-testid="helper-gate-login" className="hf-btn hf-btn-primary mt-8 w-full">
        Email me a sign-in link
      </Link>
    </div>
  );
}
